import { useEffect } from "react";
import { useLocation } from "wouter";

const BASE_URL = "https://nokuhiro.com";

/**
 * Dynamically updates the canonical tag and hreflang tags based on the current route.
 * This ensures each locale page has a self-referencing canonical (required for proper indexing)
 * while maintaining correct hreflang cross-references.
 */
export function useCanonical() {
  const [location] = useLocation();

  useEffect(() => {
    // Determine the current canonical URL
    const canonicalUrl = `${BASE_URL}${location === "/" ? "/" : location}`;

    // Update canonical tag
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonicalLink) {
      canonicalLink.href = canonicalUrl;
    } else {
      canonicalLink = document.createElement("link");
      canonicalLink.rel = "canonical";
      canonicalLink.href = canonicalUrl;
      document.head.appendChild(canonicalLink);
    }

    // Determine the base path without locale prefix for hreflang generation
    const segments = location.split("/").filter(Boolean);
    const locales = ["de", "es", "it", "fr"];
    let basePath = "";

    if (locales.includes(segments[0])) {
      // Remove locale prefix to get the base path
      basePath = segments.slice(1).join("/");
    } else {
      basePath = segments.join("/");
    }

    // Only set hreflang for pages that have locale variants
    const localizablePages = ["", "privacy", "terms", "faq"];
    // Blog listing and blog articles are also localizable (same slug across locales)
    const isLocalizable = localizablePages.includes(basePath) || basePath.startsWith("blog");

    // Update hreflang tags
    const existingHreflangs = document.querySelectorAll('link[rel="alternate"][hreflang]');
    existingHreflangs.forEach((el) => el.remove());

    if (isLocalizable) {
      const hreflangs = [
        { lang: "en", href: `${BASE_URL}/${basePath}` },
        { lang: "de", href: `${BASE_URL}/de${basePath ? "/" + basePath : ""}` },
        { lang: "es", href: `${BASE_URL}/es${basePath ? "/" + basePath : ""}` },
        { lang: "it", href: `${BASE_URL}/it${basePath ? "/" + basePath : ""}` },
        { lang: "fr", href: `${BASE_URL}/fr${basePath ? "/" + basePath : ""}` },
        { lang: "x-default", href: `${BASE_URL}/${basePath}` },
      ];

      hreflangs.forEach(({ lang, href }) => {
        const link = document.createElement("link");
        link.rel = "alternate";
        link.setAttribute("hreflang", lang);
        // Clean up double slashes and trailing slashes
        link.href = href.replace(/([^:])\/\//g, "$1/").replace(/\/$/, "") || BASE_URL + "/";
        document.head.appendChild(link);
      });
    }

    // Update og:url meta tag
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute("content", canonicalUrl);
    }
  }, [location]);
}
